import { Injectable } from '@angular/core';
import { FormGroup, Validators, FormControl, FormArray } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { IEmployee } from '../interfaces/IEmployee';
import { IEmployeeWorkingContractForm } from '../interfaces/employee/IEmployeeWorkingContractForm';
import { USER_ROLES } from '../enums/UserRoles.enum';
import { IEmployeeListItem } from '../interfaces/IEmployeeListItem';
import { IWorkingHours } from '../interfaces/IWorkingHours';
import { validateDateChronology } from '../validators/date-chronology.validator';
import { EMPLOYEE_ACTIVE_FILTER } from '../enums/EmployeeActiveFilter.enum';
import { IScdItem } from '../interfaces/IScdItem';
import { TableFilterOption } from '../components/table/table.component';
import { getCustomSortNumbersStrings } from '../helpers/helpers';
import { DateService } from '../services/date.service';
import { IPaginationProps } from '../interfaces/IPaginationProps';
import { COMPARE_OPERATOR } from '../enums/CompareOperator.enum';
import { IFilter } from '../interfaces/IFilter';
import { PersonalInformationOperations } from './personalInformation.operations';
import { CONTRACT_TYPES } from '../enums/ContractTypes.enum';
import { DepartmentOperations } from './department.operations';
export const MAX_DATE_ISO = '9999-12-31T00:00:00Z';

@Injectable({
  providedIn: 'root',
})
export class EmployeeOperations {
  public buildForm(employee?: IEmployee, workingHours?: IWorkingHours[], isNew?: boolean): FormGroup {
    return new FormGroup({
      hours: new FormControl({
        value: employee ? employee.workingHoursHaveTo : 0,
        disabled: true,
      }),
      mondayHours: new FormControl({
        value: employee ? employee.workingHoursHaveTo / 5 : 0,
        disabled: true,
      }),
      tuesdayHours: new FormControl({
        value: employee ? employee.workingHoursHaveTo / 5 : 0,
        disabled: true,
      }),
      wednesdayHours: new FormControl({
        value: employee ? employee.workingHoursHaveTo / 5 : 0,
        disabled: true,
      }),
      thursdayHours: new FormControl({
        value: employee ? employee.workingHoursHaveTo / 5 : 0,
        disabled: true,
      }),
      fridayHours: new FormControl({
        value: employee ? employee.workingHoursHaveTo / 5 : 0,
        disabled: true,
      }),
      contractTypeName: new FormControl(
        {
          value: employee ? employee.contractTypeName : null,
          disabled: true,
        },
        [Validators.required],
      ),
      email: new FormControl(
        { value: employee ? employee.scdEMail : null, disabled: true },
        isNew ? [] : employee ? [Validators.required] : [],
      ),
      location: new FormControl(
        {
          value: employee ? employee.location : null,
          disabled: true,
        },
        [Validators.required],
      ),
      department: new FormControl({
        value: DepartmentOperations.fromEmployee(employee),
        disabled: true,
      }),
      qualification: new FormControl({
        value: employee ? employee.educationName : null,
        disabled: true,
      }),
      educationInfo: new FormControl({
        value: employee ? employee.educationComment : null,
        disabled: true,
      }),
      supWorkBegin: new FormControl({
        value: employee ? employee.supWorkBegin : null,
        disabled: true,
      }),
      supEmployeeBegin: new FormControl({
        value: employee ? employee.supEmployeeBegin : null,
        disabled: true,
      }),
      supRetire: new FormControl({
        value: employee ? employee.supRetire : null,
        disabled: true,
      }),
      comment: new FormControl({
        value: employee ? employee.comment : null,
        disabled: true,
      }),
      additionalInfo: new FormControl({
        value: employee ? employee.additionalInfo : '',
        disabled: true,
      }),
      passportNumber: new FormControl({
        value: employee ? employee.passportNumber : null,
        disabled: true,
      }),
      passportExpiryDate: new FormControl({
        value: employee ? employee.passportExpirationDate : null,
        disabled: true,
      }),
      userRole: new FormControl(
        {
          value: employee?.userRoleName || USER_ROLES.TEAMMITGLIED,
          disabled: true,
        },
        [Validators.required],
      ),
      admin: new FormControl({
        value: employee ? employee.isUserAdmin : null,
        disabled: true,
      }),
      group: new FormControl({ value: employee ? employee.groupName : null, disabled: true }, [Validators.required]),
      team: new FormControl({ value: employee ? employee.teamName : null, disabled: true }, [Validators.required]),
      orgTeam: new FormControl({ value: employee ? employee.orgTeamName : null, disabled: true }, [Validators.required]),
      supervisor: new FormControl({
        value: employee ? employee.supervisor : null,
        disabled: true,
      }),
      travelApprover: new FormControl({
        value: employee ? employee.travelApprover : null,
        disabled: true,
      }),
      outOfOfficeNotificationRecipients: new FormControl({
        value: employee ? employee.outOfOfficeNotificationRecipients : null,
        disabled: true,
      }),
      mentor: new FormControl({
        value: employee ? employee.mentor : null,
        disabled: true,
      }),
      countryResponsibles: new FormControl({
        value: employee ? employee.countryResponsibles : null,
        disabled: true,
      }),
      assignedEmployees: new FormControl({
        value: employee ? employee.employees : null,
        disabled: true,
      }),
      supervisorEH: new FormControl({
        value: employee ? employee.supervisorEH : null,
        disabled: true,
      }),
      isTimekeeper: new FormControl({
        value: employee ? employee.isTimekeeper : false,
        disabled: true,
      }),
      expertHouses: this.initExpertHouses(employee),
      workingContract: this.initWorkingContract(employee),
      workingHours: this.initWorkingHours(workingHours),
      visas: PersonalInformationOperations.initVisas(employee),
      qualifications: PersonalInformationOperations.initQualificationForm(employee),
    });
  }

  private initExpertHouses(employee: IEmployee): FormArray {
    const expertHouseFormGroupArray: FormGroup[] = [];

    if (employee) {
      for (let i = 0; i < employee.expertHouses.length; i++) {
        expertHouseFormGroupArray.push(
          new FormGroup({
            id: new FormControl(employee.expertHouses[i].id ? employee.expertHouses[i].id : 0),
            partnerName: new FormControl(employee.expertHouses[i].partnerName),
            hourlyRate: new FormControl(employee.expertHouses[i].hourlyRate),
            amountWorkingHours: new FormControl(employee.expertHouses[i].amountWorkingHours),
            orderValueAfterHours: new FormControl(employee.expertHouses[i].orderValueAfterHours),
            orderValueTravelCosts: new FormControl(employee.expertHouses[i].orderValueTravelCosts),
            beginDate: new FormControl(employee.expertHouses[i].beginDate),
            untilDate: new FormControl(employee.expertHouses[i].untilDate),
            pspElement: new FormControl(employee.expertHouses[i].pspElement),
            cycleNumber: new FormControl(employee.expertHouses[i].cycleNumber),
            sapOrderNumber: new FormControl(employee.expertHouses[i].sapOrderNumber),
          }),
        );
      }
    }
    return new FormArray(expertHouseFormGroupArray);
  }

  private initWorkingContract(employee?: IEmployee): IEmployeeWorkingContractForm {
    const workingContract = employee?.workingContract;

    return new FormGroup({
      id: new FormControl<number>({ value: workingContract?.id ?? 0, disabled: true }, { nonNullable: true }),
      dateOfBirth: new FormControl<Date | null>({
        value: workingContract?.dateOfBirth ? new Date(workingContract.dateOfBirth) : null,
        disabled: true,
      }),
      externalCompany: new FormControl<string | null>({ value: workingContract?.externalCompany ?? null, disabled: true }, [
        Validators.required,
      ]),
      phoneNumber: new FormControl<string | null>({ value: workingContract?.phoneNumber ?? null, disabled: true }),
      externalEmail: new FormControl<string | null>({ value: workingContract?.externalEmail ?? null, disabled: true }, [
        Validators.required,
        Validators.email,
      ]),
      comment: new FormControl<string | null>({ value: workingContract?.comment ?? null, disabled: true }),
    });
  }

  public ensureDateTime(value: any): DateTime | null {
    if (!value) {
      return null;
    }

    // If it's already a DateTime object
    if (value instanceof DateTime) {
      return value;
    }

    // If it's a string, try to parse it
    try {
      return DateTime.fromISO(value);
    } catch (e) {
      console.warn('Invalid date value:', e);
      return null;
    }
  }

  private initWorkingHours(workingHours: IWorkingHours[]): FormArray {
    const workingHoursFormGroupArray: FormGroup[] = [];
    if (workingHours) {
      for (let i = 0; i < workingHours.length; i++) {
        workingHoursFormGroupArray.push(
          new FormGroup(
            {
              id: new FormControl(workingHours[i].id ? workingHours[i].id : 0),
              workingHours: new FormControl(workingHours[i].workingHours),
              validFrom: new FormControl(new Date(workingHours[i].validFrom)),
              validTo: new FormControl(new Date(workingHours[i].validTo)),
              userId: new FormControl(workingHours[i].userId),
              mondayHours: new FormControl(workingHours[i].mondayHours),
              tuesdayHours: new FormControl(workingHours[i].tuesdayHours),
              wednesdayHours: new FormControl(workingHours[i].wednesdayHours),
              thursdayHours: new FormControl(workingHours[i].thursdayHours),
              fridayHours: new FormControl(workingHours[i].fridayHours),
              isCurrent: new FormControl(new Date(workingHours[i].validTo).getTime() === new Date(MAX_DATE_ISO).getTime()),
              isValid: new FormControl(true),
            },
            { validators: validateDateChronology('validFrom', 'validTo') },
          ),
        );
      }
    }
    return new FormArray(workingHoursFormGroupArray);
  }

  public getNewWorkingHoursForm(employee: IEmployee): FormGroup {
    const firstDayOfCurrentMonth = new Date();
    firstDayOfCurrentMonth.setDate(1);
    const firstDayOfPreviousMonth = new Date(firstDayOfCurrentMonth.getFullYear(), firstDayOfCurrentMonth.getMonth() - 1, 1);
    return new FormGroup(
      {
        id: new FormControl(0),
        workingHours: new FormControl(0),
        validFrom: new FormControl(firstDayOfPreviousMonth),
        validTo: new FormControl(firstDayOfCurrentMonth),
        userId: new FormControl(employee.id),
        mondayHours: new FormControl(0),
        tuesdayHours: new FormControl(0),
        wednesdayHours: new FormControl(0),
        thursdayHours: new FormControl(0),
        fridayHours: new FormControl(0),
        isCurrent: new FormControl(false),
        isValid: new FormControl(true),
      },
      { validators: validateDateChronology('validFrom', 'validTo') },
    );
  }

  public getNewExpertHouseForm(employee: IEmployee): FormGroup {
    return new FormGroup({
      id: new FormControl(0),
      partnerName: new FormControl(''),
      hourlyRate: new FormControl(0),
      amountWorkingHours: new FormControl(0),
      orderValueAfterHours: new FormControl(0),
      orderValueTravelCosts: new FormControl(0),
      beginDate: new FormControl(new Date()),
      untilDate: new FormControl(new Date()),
      pspElement: new FormControl(employee.expertHouses && employee.expertHouses[0] ? employee.expertHouses[0].pspElement : ''),
      cycleNumber: new FormControl(0),
      sapOrderNumber: new FormControl(''),
    });
  }

  public setRequired(value: string, formGroup: FormGroup) {
    const workingContract = formGroup.get('workingContract');
    if (value === CONTRACT_TYPES.WorkingContract) {
      workingContract.enable();
    } else {
      workingContract.disable();
    }

    if (
      value === CONTRACT_TYPES.Experthouse ||
      value === CONTRACT_TYPES.Zukauf ||
      value === CONTRACT_TYPES.Student ||
      value === CONTRACT_TYPES.Fachberatung
    ) {
      formGroup.get('supWorkBegin').addValidators(Validators.required);
      formGroup.get('supEmployeeBegin').clearValidators();
    } else if (value === CONTRACT_TYPES.Festangestellter) {
      formGroup.get('supEmployeeBegin').addValidators(Validators.required);
      formGroup.get('supWorkBegin').clearValidators();
    } else {
      formGroup.get('supWorkBegin').clearValidators();
      formGroup.get('supEmployeeBegin').clearValidators();
    }
  }

  public getEmptyEmployee(dateService: DateService): IEmployee {
    return {
      id: 0,
      approvalFleetVehicle: false,
      approvalPrivateCar: false,
      contractType: 0,
      contractTypeName: '',
      groupName: '',
      hasHealthCheck: false,
      hasOngoingWorkflow: false,
      ongoingWorkflowId: null,
      isEuP: false,
      isElectrician: false,
      isFirstResponder: false,
      lastApprovedMonth: this.getLastDayPreviousMonth(dateService),
      lastMonthlySettlement: this.getLastDayPreviousMonth(dateService),
      location: '',
      orgTeamName: '',
      passportNumber: '',
      passportExpirationDate: null,
      passiveEmployments: [],
      retired: '',
      scdGid: '',
      scdEMail: '',
      scdName: '',
      supWorkBegin: new Date(),
      supEmployeeBegin: new Date(),
      supRetire: new Date(),
      teamName: '',
      userRoleName: USER_ROLES.TEAMMITGLIED,
      workingHoursHaveTo: 0,
      additionalInfo: '',
      assignedEmployees: [],
      assignedEmployeesEH: [],
      comment: '',
      controlledByGroup: [],
      country: '',
      createdAt: new Date(),
      education: 0,
      educationName: '',
      educationComment: '',
      emergencyContacts: [],
      employeeVisas: [],
      employees: [],
      employeesEH: [],
      expertHouses: [],
      filterPartner: '',
      filterGroup: '',
      filterTeam: '',
      hourlyRate: 0,
      isUserAdmin: false,
      orderValueAfterHours: 0,
      orderValueTravelCosts: 0,
      pspElement: '',
      supervisor: [],
      supervisorEH: [],
      travelApprover: null,
      outOfOfficeNotificationRecipients: [],
      mentor: null,
      hasDriverLicense: false,
      hasTvInOrgChart: false,
      isTimekeeper: false,
      countryResponsibles: [],
    };
  }

  private getLastDayPreviousMonth(dateService: DateService): string {
    const currentDate = new Date();

    if (currentDate.getMonth() === 0) {
      return dateService.toIsoStringWithoutTimeZone(new Date(currentDate.getFullYear() - 1, 11, 0));
    } else {
      return dateService.toIsoStringWithoutTimeZone(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 0));
    }
  }

  public getEmployeeListForUser(employees: IEmployeeListItem[], userEmployee: IEmployee): IEmployeeListItem[] {
    if (employees.length) {
      let filteredEmployees = [employees.find(employee => employee.scdGid === userEmployee.scdGid)];
      switch (userEmployee.userRoleName) {
        case USER_ROLES.BUDGETVERANTWORTLICHER:
          {
            filteredEmployees = employees.filter(employee => employee.orgTeamName === userEmployee.orgTeamName);
          }
          break;
        case USER_ROLES.GRUPPENLEITER:
          {
            filteredEmployees = employees.filter(employee => employee.groupName === userEmployee.groupName);
          }
          break;
        case USER_ROLES.TECHNISCHER_CONTROLLER:
          {
            filteredEmployees = employees;
          }
          break;
      }
      if (
        (userEmployee.employees.length && userEmployee.userRoleName === USER_ROLES.BUDGETVERANTWORTLICHER) ||
        userEmployee.userRoleName === USER_ROLES.GRUPPENLEITER ||
        userEmployee.userRoleName === USER_ROLES.TECHNISCHER_CONTROLLER
      ) {
        userEmployee.employees.forEach(assignedEmployee => {
          if (!filteredEmployees.find(employee => employee.scdGid === assignedEmployee.scdGid)) {
            const foundEmployee = employees.find(employee => employee.scdGid === assignedEmployee.scdGid);
            if (foundEmployee) {
              filteredEmployees.push(foundEmployee);
            }
          }
        });
      }
      return filteredEmployees?.sort((employee1, employee2) => getCustomSortNumbersStrings(employee1.scdName, employee2.scdName));
    }
    return [];
  }

  public sortByActiveStatusAndName(employees: IEmployeeListItem[], activeFilter: EMPLOYEE_ACTIVE_FILTER): void {
    if (activeFilter === EMPLOYEE_ACTIVE_FILTER.GET_ALL) {
      employees.sort((a, b) => {
        const aIsActive = a.supRetire === null;
        const bIsActive = b.supRetire === null;
        if (aIsActive !== bIsActive) {
          return aIsActive ? -1 : 1;
        }
        return a.scdName.localeCompare(b.scdName);
      });
    } else {
      employees.sort((a, b) => a.scdName.localeCompare(b.scdName));
    }
  }

  public static updateEmployeeDataFromScd(employee: IEmployee, scdRecords: IScdItem[]): void {
    scdRecords.forEach(scdRecord => {
      if (scdRecord?.surName && scdRecord?.firstName) {
        employee.scdName = scdRecord.surName + ', ' + scdRecord.firstName;
        employee.scdEMail = scdRecord.mail;
      }
    });
  }

  public static getUserRoleNameOptions(employees: IEmployeeListItem[], translateService: TranslateService): TableFilterOption[] {
    const translatedEmployeeRoles = employees.reduce((acc: string[], employee: IEmployeeListItem) => {
      if (employee.userRoleName && !acc.includes(translateService.instant(`employees.${employee.userRoleName}`) as string)) {
        acc.push(translateService.instant(`employees.${employee.userRoleName}`) as string);
      }
      return acc;
    }, []);

    return translatedEmployeeRoles.map(role => {
      return { value: role, label: role };
    });
  }

  public static getFrontendCalculatedFilterOptions(column: string): TableFilterOption[] {
    if (column !== 'hasOngoingWorkflow') {
      return [];
    }

    return [
      { label: 'true', value: 'true' },
      { label: 'false', value: 'false' },
    ];
  }

  public static buildEmployeePaginationProps(
    pageSize: number,
    filters: IFilter[],
    lastRead?: number,
    sort?: string,
    sortedLastReadValue?: string,
    searchValue?: string,
    comparisonOperator?: COMPARE_OPERATOR | '',
    searchFields?: string[],
    onlyActive?: boolean,
    isForPicker?: boolean,
  ): IPaginationProps {
    // Input validation
    if (!pageSize || pageSize <= 0) {
      throw new Error('pageSize must be a positive number');
    }

    if (!Array.isArray(filters)) {
      throw new Error('filters must be an array');
    }

    return {
      pageSize: Math.max(1, pageSize),
      filters,
      lastRead: lastRead ?? 0,
      sort: sort ?? null,
      sortedLastReadValue: sortedLastReadValue ?? null,
      searchValue: searchValue ?? null,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      comparisonOperator: comparisonOperator ?? null,
      searchFields: searchFields ?? [],
      onlyActive,
      isForPicker,
    };
  }
}
